import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, Calculator, HeartPulse, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { buildHeartRiskPrefill } from "@/lib/heartRisk/prefill";
import { calculatePceRisk } from "@/lib/heartRisk/pce";
import type { HeartRiskFieldSource, HeartRiskFieldSources, HeartRiskInput, HeartRiskResult } from "@/lib/heartRisk/types";
import { validatePceInput } from "@/lib/heartRisk/validation";

type HeartRiskFormState = {
  age: string;
  sex: HeartRiskInput["sex"] | "";
  race: HeartRiskInput["race"];
  systolicBp: string;
  diastolicBp: string;
  totalCholesterol: string;
  hdlCholesterol: string;
  diabetes: boolean;
  smoker: boolean;
  onHypertensionTreatment: boolean;
};

type PatientSummary = {
  id: string;
  full_name: string;
  data_nascimento: string | null;
  sexo: string | null;
};

const DEFAULT_FORM: HeartRiskFormState = {
  age: "",
  sex: "",
  race: "other",
  systolicBp: "",
  diastolicBp: "",
  totalCholesterol: "",
  hdlCholesterol: "",
  diabetes: false,
  smoker: false,
  onHypertensionTreatment: false,
};

const SOURCE_LABELS: Record<HeartRiskFieldSource, string> = {
  from_profile: "Último cálculo",
  from_patient: "Cadastro",
  from_lab_result: "Exames",
  manual: "Manual",
};

function sourceBadge(source?: HeartRiskFieldSource) {
  if (!source) return null;
  return (
    <Badge variant="outline" className="text-xs font-normal">
      {SOURCE_LABELS[source]}
    </Badge>
  );
}

export default function HeartRiskExtension() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPatientPortalRoute = location.pathname.startsWith("/paciente");
  const isProfessionalRoute = !isPatientPortalRoute;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [patient, setPatient] = useState<PatientSummary | null>(null);
  const [professionalPatients, setProfessionalPatients] = useState<PatientSummary[]>([]);
  const [selectedProfessionalPatientId, setSelectedProfessionalPatientId] = useState("");
  const [form, setForm] = useState<HeartRiskFormState>(DEFAULT_FORM);
  const [fieldSources, setFieldSources] = useState<HeartRiskFieldSources>({});
  const [result, setResult] = useState<HeartRiskResult | null>(null);
  const [lastCalculatedAt, setLastCalculatedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const patientIdFromQuery = searchParams.get("patientId");

  const categoryLabel = useMemo(() => {
    if (!result) return null;
    if (result.category === "low") return "Baixo risco";
    if (result.category === "borderline") return "Risco limítrofe";
    if (result.category === "intermediate") return "Risco intermediário";
    return "Alto risco";
  }, [result]);

  const patientDetailsPath = useMemo(() => {
    if (!patient) return null;
    return isPatientPortalRoute ? `/paciente/minha-saude/${patient.id}` : `/app/pacientes/${patient.id}`;
  }, [isPatientPortalRoute, patient]);

  useEffect(() => {
    if (isProfessionalRoute) {
      setSelectedProfessionalPatientId(patientIdFromQuery ?? "");
    }
  }, [isProfessionalRoute, patientIdFromQuery]);

  useEffect(() => {
    void loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, patientIdFromQuery, selectedProfessionalPatientId]);

  const markFieldAsManual = (field: keyof HeartRiskInput) => {
    setFieldSources((prev) => ({ ...prev, [field]: "manual" }));
  };

  const updateForm = <K extends keyof HeartRiskFormState>(field: K, value: HeartRiskFormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        setError("Usuário não autenticado.");
        return;
      }

      const patientResult = await resolvePatient(user.id);
      if (!patientResult) {
        setPatient(null);
        setForm(DEFAULT_FORM);
        setFieldSources({});
        setResult(null);
        setLastCalculatedAt(null);
        return;
      }
      setPatient(patientResult);

      const [profileResponse, calcResponse, labsResponse] = await Promise.all([
        supabase
          .from("patient_heart_risk_profiles")
          .select(
            "age, sex, race, systolic_bp, diastolic_bp, total_cholesterol, hdl_cholesterol, diabetes, smoker, on_hypertension_treatment",
          )
          .eq("owner_id", user.id)
          .eq("patient_id", patientResult.id)
          .maybeSingle(),
        supabase
          .from("patient_heart_risk_calculations")
          .select("result_snapshot, calculated_at")
          .eq("owner_id", user.id)
          .eq("patient_id", patientResult.id)
          .order("calculated_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from("lab_results_onboarding")
          .select("test_name, value, value_text, collected_at, created_at")
          .eq("patient_id", patientResult.id)
          .order("collected_at", { ascending: false, nullsFirst: false })
          .order("created_at", { ascending: false }),
      ]);

      if (profileResponse.error) throw profileResponse.error;
      if (calcResponse.error) throw calcResponse.error;
      if (labsResponse.error) throw labsResponse.error;

      const prefill = buildHeartRiskPrefill({
        patient: patientResult,
        profile: profileResponse.data,
        labs: labsResponse.data ?? [],
      });

      setFieldSources(prefill.fieldSources);
      setForm({
        age: prefill.input.age != null ? String(prefill.input.age) : "",
        sex: prefill.input.sex ?? "",
        race: prefill.input.race ?? "other",
        systolicBp: prefill.input.systolicBp != null ? String(prefill.input.systolicBp) : "",
        diastolicBp: prefill.input.diastolicBp != null ? String(prefill.input.diastolicBp) : "",
        totalCholesterol: prefill.input.totalCholesterol != null ? String(prefill.input.totalCholesterol) : "",
        hdlCholesterol: prefill.input.hdlCholesterol != null ? String(prefill.input.hdlCholesterol) : "",
        diabetes: prefill.input.diabetes ?? false,
        smoker: prefill.input.smoker ?? false,
        onHypertensionTreatment: prefill.input.onHypertensionTreatment ?? false,
      });

      if (calcResponse.data?.result_snapshot) {
        const snapshot = calcResponse.data.result_snapshot as Partial<HeartRiskResult>;
        if (
          typeof snapshot.tenYearRisk === "number" &&
          typeof snapshot.optimalTenYearRisk === "number" &&
          typeof snapshot.category === "string"
        ) {
          setResult({
            tenYearRisk: snapshot.tenYearRisk,
            optimalTenYearRisk: snapshot.optimalTenYearRisk,
            lifetimeRisk: typeof snapshot.lifetimeRisk === "number" ? snapshot.lifetimeRisk : null,
            category: snapshot.category as HeartRiskResult["category"],
          });
        }
      }

      setLastCalculatedAt(calcResponse.data?.calculated_at ?? null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro ao carregar calculadora.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const resolvePatient = async (userId: string): Promise<PatientSummary | null> => {
    if (isPatientPortalRoute) {
      const { data, error } = await supabase
        .from("patients_onboarding")
        .select("id, full_name, data_nascimento, sexo")
        .eq("owner_id", userId)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        setError("Não foi encontrado cadastro de paciente vinculado ao seu usuário.");
        return null;
      }

      return data;
    }

    const { data: ownedPatients, error: ownedPatientsError } = await supabase
      .from("patients_onboarding")
      .select("id, full_name, data_nascimento, sexo")
      .eq("owner_id", userId)
      .eq("status", "active")
      .order("full_name", { ascending: true });

    if (ownedPatientsError) throw ownedPatientsError;
    setProfessionalPatients(ownedPatients ?? []);

    const targetPatientId = selectedProfessionalPatientId || patientIdFromQuery;
    if (!targetPatientId) {
      return null;
    }

    const selectedPatient = (ownedPatients ?? []).find((ownedPatient) => ownedPatient.id === targetPatientId);
    if (!selectedPatient) {
      return null;
    }

    return selectedPatient;
  };

  const buildInput = (): HeartRiskInput | null => {
    if (!form.sex) {
      toast.error("Selecione o sexo para calcular o risco.");
      return null;
    }

    const input: HeartRiskInput = {
      age: Number(form.age),
      sex: form.sex,
      race: form.race,
      systolicBp: Number(form.systolicBp),
      diastolicBp: Number(form.diastolicBp),
      totalCholesterol: Number(form.totalCholesterol),
      hdlCholesterol: Number(form.hdlCholesterol),
      diabetes: form.diabetes,
      smoker: form.smoker,
      onHypertensionTreatment: form.onHypertensionTreatment,
    };

    const numericKeys: Array<keyof Pick<HeartRiskInput, "age" | "systolicBp" | "diastolicBp" | "totalCholesterol" | "hdlCholesterol">> =
      ["age", "systolicBp", "diastolicBp", "totalCholesterol", "hdlCholesterol"];

    const hasInvalid = numericKeys.some((key) => Number.isNaN(input[key]));
    if (hasInvalid) {
      toast.error("Preencha todos os campos numéricos antes de calcular.");
      return null;
    }

    return input;
  };

  const handleCalculate = async () => {
    if (!patient) return;

    const input = buildInput();
    if (!input) return;

    const validation = validatePceInput(input);
    if (!validation.valid) {
      validation.errors.forEach((item) => toast.error(item));
      return;
    }

    setSaving(true);
    try {
      const calculated = calculatePceRisk(input);
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) throw new Error("Usuário não autenticado.");

      const fieldSourcePayload = {
        age: fieldSources.age ?? "manual",
        sex: fieldSources.sex ?? "manual",
        race: fieldSources.race ?? "manual",
        systolicBp: fieldSources.systolicBp ?? "manual",
        diastolicBp: fieldSources.diastolicBp ?? "manual",
        totalCholesterol: fieldSources.totalCholesterol ?? "manual",
        hdlCholesterol: fieldSources.hdlCholesterol ?? "manual",
        diabetes: fieldSources.diabetes ?? "manual",
        smoker: fieldSources.smoker ?? "manual",
        onHypertensionTreatment: fieldSources.onHypertensionTreatment ?? "manual",
      };

      const { data: profile, error: profileError } = await supabase
        .from("patient_heart_risk_profiles")
        .upsert(
          {
            owner_id: user.id,
            patient_id: patient.id,
            age: input.age,
            sex: input.sex,
            race: input.race,
            systolic_bp: input.systolicBp,
            diastolic_bp: input.diastolicBp,
            total_cholesterol: input.totalCholesterol,
            hdl_cholesterol: input.hdlCholesterol,
            diabetes: input.diabetes,
            smoker: input.smoker,
            on_hypertension_treatment: input.onHypertensionTreatment,
            field_sources: fieldSourcePayload,
            last_input_at: new Date().toISOString(),
          },
          { onConflict: "owner_id,patient_id" },
        )
        .select("id")
        .single();

      if (profileError) throw profileError;

      const { error: calculationError } = await supabase.from("patient_heart_risk_calculations").insert({
        owner_id: user.id,
        patient_id: patient.id,
        profile_id: profile.id,
        model: "pce",
        input_snapshot: input as unknown as Json,
        result_snapshot: calculated as unknown as Json,
        category: calculated.category,
      });

      if (calculationError) throw calculationError;

      setResult(calculated);
      setLastCalculatedAt(new Date().toISOString());
      toast.success("Cálculo realizado e salvo com sucesso.");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro ao calcular risco.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Não foi possível carregar</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (isPatientPortalRoute && !patient) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Paciente não identificado</AlertTitle>
        <AlertDescription>Não foi possível carregar um paciente para esta extensão.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center">
          <HeartPulse className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Estimativa de Risco Cardiovascular</h1>
          {patient && <p className="text-sm text-muted-foreground mt-1">Paciente: {patient.full_name}</p>}
        </div>
      </div>

      {isProfessionalRoute && (
        <Card>
          <CardHeader>
            <CardTitle>Paciente para cálculo</CardTitle>
            <CardDescription>Selecione o paciente para preencher os dados e calcular o risco.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="patient-selector">Paciente</Label>
              <Select value={selectedProfessionalPatientId} onValueChange={setSelectedProfessionalPatientId}>
                <SelectTrigger id="patient-selector">
                  <SelectValue
                    placeholder={
                      professionalPatients.length === 0
                        ? "Nenhum paciente ativo cadastrado"
                        : "Selecione um paciente"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {professionalPatients.map((ownedPatient) => (
                    <SelectItem key={ownedPatient.id} value={ownedPatient.id}>
                      {ownedPatient.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {professionalPatients.length === 0 && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Você ainda não possui pacientes ativos cadastrados para usar esta calculadora.
                </AlertDescription>
              </Alert>
            )}

            {professionalPatients.length > 0 && !patient && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>Selecione um paciente para continuar com o cálculo.</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {patient && (
        <Card>
        <CardHeader>
          <CardTitle>Dados para cálculo (Pooled Cohort)</CardTitle>
          <CardDescription>
            Revise os campos pré-preenchidos. Alterações manuais ficam salvas para o próximo cálculo.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="age">Idade (anos)</Label>
                {sourceBadge(fieldSources.age)}
              </div>
              <Input
                id="age"
                type="number"
                value={form.age}
                onChange={(e) => {
                  updateForm("age", e.target.value);
                  markFieldAsManual("age");
                }}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="sex">Sexo</Label>
                {sourceBadge(fieldSources.sex)}
              </div>
              <Select
                value={form.sex}
                onValueChange={(value: HeartRiskInput["sex"]) => {
                  updateForm("sex", value);
                  markFieldAsManual("sex");
                }}
              >
                <SelectTrigger id="sex">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Masculino</SelectItem>
                  <SelectItem value="female">Feminino</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="race">Raça</Label>
                {sourceBadge(fieldSources.race)}
              </div>
              <Select
                value={form.race}
                onValueChange={(value: HeartRiskInput["race"]) => {
                  updateForm("race", value);
                  markFieldAsManual("race");
                }}
              >
                <SelectTrigger id="race">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="white">Branca</SelectItem>
                  <SelectItem value="black">Negra</SelectItem>
                  <SelectItem value="other">Outra/Não informado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="sbp">Pressão sistólica (mmHg)</Label>
                {sourceBadge(fieldSources.systolicBp)}
              </div>
              <Input
                id="sbp"
                type="number"
                value={form.systolicBp}
                onChange={(e) => {
                  updateForm("systolicBp", e.target.value);
                  markFieldAsManual("systolicBp");
                }}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="dbp">Pressão diastólica (mmHg)</Label>
                {sourceBadge(fieldSources.diastolicBp)}
              </div>
              <Input
                id="dbp"
                type="number"
                value={form.diastolicBp}
                onChange={(e) => {
                  updateForm("diastolicBp", e.target.value);
                  markFieldAsManual("diastolicBp");
                }}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="tc">Colesterol total (mg/dL)</Label>
                {sourceBadge(fieldSources.totalCholesterol)}
              </div>
              <Input
                id="tc"
                type="number"
                value={form.totalCholesterol}
                onChange={(e) => {
                  updateForm("totalCholesterol", e.target.value);
                  markFieldAsManual("totalCholesterol");
                }}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="hdl">HDL (mg/dL)</Label>
                {sourceBadge(fieldSources.hdlCholesterol)}
              </div>
              <Input
                id="hdl"
                type="number"
                value={form.hdlCholesterol}
                onChange={(e) => {
                  updateForm("hdlCholesterol", e.target.value);
                  markFieldAsManual("hdlCholesterol");
                }}
              />
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="flex items-center gap-2 rounded-md border p-3 cursor-pointer">
              <Checkbox
                checked={form.diabetes}
                onCheckedChange={(checked) => {
                  updateForm("diabetes", Boolean(checked));
                  markFieldAsManual("diabetes");
                }}
              />
              <span className="text-sm">Diabetes</span>
            </label>
            <label className="flex items-center gap-2 rounded-md border p-3 cursor-pointer">
              <Checkbox
                checked={form.smoker}
                onCheckedChange={(checked) => {
                  updateForm("smoker", Boolean(checked));
                  markFieldAsManual("smoker");
                }}
              />
              <span className="text-sm">Tabagista atual</span>
            </label>
            <label className="flex items-center gap-2 rounded-md border p-3 cursor-pointer">
              <Checkbox
                checked={form.onHypertensionTreatment}
                onCheckedChange={(checked) => {
                  updateForm("onHypertensionTreatment", Boolean(checked));
                  markFieldAsManual("onHypertensionTreatment");
                }}
              />
              <span className="text-sm">Uso de anti-hipertensivo</span>
            </label>
          </div>

          <Button onClick={handleCalculate} disabled={saving} className="w-full" size="lg">
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Calculando...
              </>
            ) : (
              <>
                <Calculator className="h-4 w-4 mr-2" />
                Calcular e salvar revisão
              </>
            )}
          </Button>
        </CardContent>
      </Card>
      )}

      {result && (
        <Card>
          <CardHeader>
            <CardTitle>Resultado do risco ASCVD</CardTitle>
            <CardDescription>
              {lastCalculatedAt
                ? `Último cálculo em ${new Date(lastCalculatedAt).toLocaleString("pt-BR")}`
                : "Resultado calculado agora"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-lg border p-4">
                <p className="text-xs text-muted-foreground mb-1">Risco 10 anos</p>
                <p className="text-3xl font-bold text-primary">{result.tenYearRisk.toFixed(1)}%</p>
                {categoryLabel && <p className="text-xs mt-1">{categoryLabel}</p>}
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-xs text-muted-foreground mb-1">Risco ótimo 10 anos</p>
                <p className="text-3xl font-bold">{result.optimalTenYearRisk.toFixed(1)}%</p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-xs text-muted-foreground mb-1">Risco ao longo da vida</p>
                <p className="text-3xl font-bold">
                  {result.lifetimeRisk == null ? "N/A" : `${result.lifetimeRisk.toFixed(1)}%`}
                </p>
                {result.lifetimeRisk == null && (
                  <p className="text-xs mt-1 text-muted-foreground">Disponível para 20-59 anos</p>
                )}
              </div>
            </div>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Esta estimativa apoia a decisão clínica e não substitui avaliação médica individualizada.
              </AlertDescription>
            </Alert>
            {patientDetailsPath && (
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => navigate(patientDetailsPath)}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Voltar para paciente
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
